import { Col, Empty, Form, Row, Select } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import {
  CommonAlignCenterContainer,
  CommonButton,
  CommonDatePicker,
  CommonFlexContainer,
  CommonInput,
  CommonMaskInput,
  CommonOption,
  CommonSelect,
  CommonTextMedium,
} from "../../../components/common/AtomCommonComponents";
import {
  CommonFormItemWithLabel,
  CommonModal,
} from "../../../components/common/MoleculesCommonComponent";
import CustomerService from "../../../services/api/CustomerService";
import DialogUtils from "../../../utils/DialogUtils";
import ValidationUtils from "../../../utils/ValidationUtils";
import TextFormatUtils from "../../../utils/TextFormatUtils";
import {
  initialAddressModalState,
  validateContactNumber,
  namePattern,
} from "../../../utils/PatternUtils";
import { CheckIcon } from "../../../components/icons/Icons";
import { CheckCircle, SubscriptionContainer } from "./CustomerModalViewStyles";
import {
  AddEditAddressModalType,
  ProductAllergiesType,
} from "../../../types/types";
import AddEditAddressModal from "../../../components/common/modals/AddEditAddressModal";
import ProgramsService from "../../../services/api/ProgramService";
import APPConfig from "../../../config";
import HomeDeliveryService from "../../../services/api/HomeDeliveryService";
import moment from "moment";
import DateUtils from "../../../utils/DateUtils";
import { RangePickerProps } from "antd/lib/date-picker";
const { INITIAL_GEOLOCATION_LAT, INITIAL_GEOLOCATION_LONG } = APPConfig;

const { Option } = Select;

interface ProgramItemType {
  program_id: string;
  name: string;
}

interface AddEditCustomerModalProps {
  isAddEditCustomerModalVisible: boolean;
  onCompleteModal: any;
  onCancelModal: () => void;
  editData?: any;
}

const AddEditCustomerModal: React.FC<AddEditCustomerModalProps> = (props) => {
  const [form] = Form.useForm();
  const [mode, setMode] = useState<string>("add");
  const [isOther, setIsOther] = useState(false);
  const [isSubscribe, setIsSubscribe] = useState(false);
  const [programs, setPrograms] = useState<{ value: string; label: string }[]>(
    []
  );
  const [allergens, setAllergens] = useState();

  const [editDataAdressModal, setEditDataAdressModal] =
    useState<AddEditAddressModalType>(initialAddressModalState());
  const [addDataAddressModal, setAddDataAddressModal] =
    useState<AddEditAddressModalType>(initialAddressModalState());

  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [hasAllergens, setHasAllergens] = useState<boolean>(false);

  const [isAddEditAddressModal, setIsAddEditAddressModal] =
    useState<boolean>(false);

  let ethnicity = [
    "American Indian or Alaska Native",
    "Asian",
    "Black or African American",
    "Native Hawaiian or Other Pacific Islander",
    "White",
    "Others",
  ];

  const customerService = useMemo(() => new CustomerService(), []);
  const programService = useMemo(() => new ProgramsService(), []);
  const homeDeliveryService = useMemo(() => new HomeDeliveryService(), []);

  useEffect(() => {
    const initialize = () => {
      if (props.isAddEditCustomerModalVisible) {
        if (props.editData) {
          let data = props.editData;
          setMode("edit");
          setEditDataAdressModal({
            address_1: data?.address?.address_1 ?? "",
            address_2: data?.address?.address_2 ?? "",
            city: data?.address?.city ?? "",
            country: "United States",
            zip_code: data?.address?.zip_code ?? "",
            state: data?.address?.state ?? "",
            geolocation: data?.address?.geolocation ?? [
              Number(INITIAL_GEOLOCATION_LAT),
              Number(INITIAL_GEOLOCATION_LONG),
            ],
          });

          if (
            props.editData.ethnicity &&
            !ethnicity.includes(props.editData.ethnicity)
          ) {
            setIsOther(true);
            data.others = props.editData.ethnicity;
            props.editData.ethnicity = "Others";
          }
          setIsSubscribe(props.editData.is_subscribe);
          setSelectedPrograms(
            props.editData.programs?.map(
              (item: ProgramItemType) => item.program_id
            ) || []
          );
          form.setFieldsValue({
            ...data,
            household_member_count: props.editData.household_member_count
              ? props.editData.household_member_count
              : null,
            contact_no: TextFormatUtils.formatContactNumberForEditDisplay(
              props.editData
            ),
            programs: props.editData.programs?.map(
              (item: ProgramItemType) => item.program_id
            ),
            allergens: props.editData.product_allergies?.map(
              (item: ProductAllergiesType) => item.product_id
            ),
          });
        } else {
          setMode("add");
          setIsSubscribe(false);
          setIsOther(false);
          form.resetFields();
        }
      }
    };
    initialize();
  }, [props.isAddEditCustomerModalVisible, form, props.editData]);

  useEffect(() => {
    fetchPrograms();
    fetchAllergens();
  }, [props?.editData]);

  useEffect(() => {
    const zipCodeEdit = editDataAdressModal.zip_code;
    const zipCodeAdd = addDataAddressModal.zip_code;

    if (zipCodeAdd) {
      form.setFieldsValue({ zip_code: zipCodeAdd });
      return;
    }

    if (zipCodeEdit) {
      form.setFieldsValue({ zip_code: zipCodeEdit });
      return;
    }
  }, [editDataAdressModal, addDataAddressModal]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const contactNumber = TextFormatUtils.translatePhoneNumber(
        values.contact_no
      );
      if (values.ethnicity === "Others") {
        values.ethnicity = values.others ? values.others : "";
      }

      if (mode === "add") {
        if (!addDataAddressModal.address_1 && !addDataAddressModal.city) {
          DialogUtils.error("Address is required.");
          return;
        }
        const customer = await customerService.add(
          values.first_name,
          values.last_name,
          values.email,
          contactNumber,
          values.household_member_count ? values.household_member_count : 0,
          values.ethnicity,
          values.loyalty_number,
          values.membership_status,
          addDataAddressModal.zip_code
            ? addDataAddressModal.zip_code
            : values.zip_code,
          addDataAddressModal.address_1,
          addDataAddressModal.address_2,
          addDataAddressModal.city,
          addDataAddressModal.country,
          addDataAddressModal.state,
          addDataAddressModal.geolocation,
          isSubscribe,
          values.gender,
          values.programs,
          values.allergens
        );
        setAddDataAddressModal(initialAddressModalState());
        setEditDataAdressModal(initialAddressModalState());
        DialogUtils.success("Customer has been successfully added.");
        props.onCompleteModal(customer._id);
      } else if (mode === "edit" && props.editData && props.editData._id) {
        if (!editDataAdressModal.address_1 && !editDataAdressModal.city) {
          DialogUtils.error("Address is required.");
          return;
        }

        await customerService.edit(
          values.first_name,
          values.last_name,
          values.email,
          contactNumber,
          values.household_member_count ? values.household_member_count : 0,
          values.ethnicity,
          values.loyalty_number,
          values.membership_status,
          editDataAdressModal.zip_code
            ? editDataAdressModal.zip_code
            : values.zip_code,
          editDataAdressModal.address_1,
          editDataAdressModal.address_2,
          editDataAdressModal.city,
          editDataAdressModal.country,
          editDataAdressModal.state,
          editDataAdressModal.geolocation,
          isSubscribe,
          props.editData._id,
          values.gender,
          values.programs,
          values.allergens
        );
        setAddDataAddressModal(initialAddressModalState());
        setEditDataAdressModal(initialAddressModalState());
        DialogUtils.success("Customer has been successfully updated.");
        props.onCompleteModal();
      } else {
        DialogUtils.error("Something went wrong!");
        return;
      }

      form.resetFields();
    } catch (error) {
      return;
    }
  };

  const fetchPrograms = async () => {
    try {
      const result = await programService.getPrograms();
      const programs = result.map(
        ({ _id, name }: { _id: string; name: string }) => ({
          value: _id,
          label: name,
          // disabled: props?.editData?.programs?.some((x: any) => x?.program_id?.toString() === _id.toString())
        })
      );
      setPrograms(programs);
    } catch (error) {
      DialogUtils.error(`Failed to fetch branches: ${error}`);
    }
  };

  const fetchAllergens = async () => {
    try {
      const result = await homeDeliveryService.getProductNames("");
      const allergens = result.map(
        ({ id, value }: { id: string; value: string }) => ({
          value: id,
          label: value,
        })
      );
      setAllergens(allergens);
    } catch (error) {
      DialogUtils.error(`Failed to fetch branches: ${error}`);
    }
  };

  const isMedicAid = selectedPrograms?.some((programId: string) =>
    programs?.find(
      (p: any) => p.value === programId && p.label.toLowerCase() === "medicaid"
    )
  );

  const renderAddressEdit = mode === "edit" &&
    TextFormatUtils.addressString(editDataAdressModal) && (
      <CommonAlignCenterContainer
        isAddressContainer
        justifyCenter
        margin="0px 0px 10px 0px"
      >
        <CommonTextMedium color="primary">
          {TextFormatUtils.addressString(editDataAdressModal)}
        </CommonTextMedium>
      </CommonAlignCenterContainer>
    );

  const renderAddressAdd = mode === "add" &&
    TextFormatUtils.addressString(addDataAddressModal) && (
      <CommonAlignCenterContainer
        isAddressContainer
        justifyCenter
        margin="0px 0px 10px 0px"
      >
        <CommonTextMedium color="primary">
          {TextFormatUtils.addressString(addDataAddressModal)}
        </CommonTextMedium>
      </CommonAlignCenterContainer>
    );

  const onOpenEditAddressInput = async () => {
    setIsAddEditAddressModal(true);
  };

  const onOpenAddEditAddressMap = () => {
    setIsAddEditAddressModal(true);
  };

  const onEthnicityChange = (data: any) => {
    if (data === "Others") {
      setIsOther(true);
    } else {
      setIsOther(false);
    }
  };

  const onCloseModal = () => {
    form.resetFields();
    setAddDataAddressModal(initialAddressModalState());
    setEditDataAdressModal(initialAddressModalState());
    props.onCancelModal();
  };

  const onCloseAddEditAddressModal = async () => {
    setIsAddEditAddressModal(false);
  };

  const onClickDeleteAddress = () => {
    setEditDataAdressModal(initialAddressModalState());
    form.resetFields(["zip_code"]);
  };

  return (
    <>
      <CommonModal
        maskClosable={false}
        title={mode === "edit" ? "Edit Customer" : "Add Customer"}
        visible={props.isAddEditCustomerModalVisible}
        onCancel={onCloseModal}
        footer={[
          <CommonButton key="cancel" onClick={onCloseModal}>
            Cancel
          </CommonButton>,
          <CommonButton
            key="save"
            color="white"
            background="primary"
            onClick={onSubmit}
          >
            Save
          </CommonButton>,
        ]}
      >
        <Form form={form} validateTrigger="onChange" onFinish={onSubmit}>
          <CommonFormItemWithLabel
            label={"Loyalty Number"}
            name="loyalty_number"
            rules={[
              { validator: ValidationUtils.validateInput },
              {
                validator: (rule, value) =>
                  ValidationUtils.validateCharactersLimit(value, 30),
              },
            ]}
          >
            <CommonInput
              color="primary"
              maxLength={30}
              placeholder="Input Loyalty Number"
            />
          </CommonFormItemWithLabel>
          <div id="select">
            <CommonFormItemWithLabel
              label={"Membership Status"}
              name="membership_status"
              rules={[
                { required: true, message: "Membership Status is required." },
                { validator: ValidationUtils.validateLetterInput },
              ]}
            >
              <CommonSelect
                color="primary"
                placeholder="Select Membership Status"
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
              >
                <CommonOption key={"active"} value={"active"}>
                  Active
                </CommonOption>
                <CommonOption key={"inactive"} value={"inactive"}>
                  Inactive
                </CommonOption>
              </CommonSelect>
            </CommonFormItemWithLabel>
          </div>
          <CommonFormItemWithLabel
            label={"First Name"}
            name="first_name"
            rules={[
              { required: true, message: "First Name is required." },
              { max: 50, message: "Input must be at most 50 characters." },
              {
                pattern: namePattern,
                message:
                  "Invalid name. Please use only letters, hyphens, apostrophes, periods, and valid accents.",
              },
            ]}
          >
            <CommonInput
              maxLength={50}
              onInput={(e: any) => {
                TextFormatUtils.upperText(e);
              }}
              color="primary"
              placeholder="Input Last Name"
            />
          </CommonFormItemWithLabel>
          <CommonFormItemWithLabel
            label={"Last Name"}
            name="last_name"
            rules={[
              { required: true, message: "Last Name is required." },
              { max: 50, message: "Input must be at most 50 characters." },
              {
                pattern: namePattern,
                message:
                  "Invalid name. Please use only letters, hyphens, apostrophes, periods, and valid accents.",
              },
            ]}
          >
            <CommonInput
              maxLength={50}
              onInput={(e: any) => {
                TextFormatUtils.upperText(e);
              }}
              color="primary"
              placeholder="Input Last Name"
            />
          </CommonFormItemWithLabel>

          <div id="select">
            <CommonFormItemWithLabel
              label={"Gender"}
              name="gender"
              rules={[
                { required: true, message: "Gender is required." },
                { validator: ValidationUtils.validateLetterInput },
              ]}
            >
              <CommonSelect
                color="primary"
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder="Select Gender"
              >
                <CommonOption key={"Male"} value={"male"}>
                  Male
                </CommonOption>
                <CommonOption key={"female"} value={"female"}>
                  Female
                </CommonOption>
              </CommonSelect>
            </CommonFormItemWithLabel>
          </div>

          <CommonFormItemWithLabel
            label={"Email"}
            name="email"
            rules={[{ validator: ValidationUtils.validateEmailInput }]}
          >
            <CommonInput color="primary" placeholder="Input Email" />
          </CommonFormItemWithLabel>

          <CommonFormItemWithLabel
            label={"Contact Number"}
            name="contact_no"
            rules={[
              { required: true, message: "Contact Number is required." },
              {
                validator: (_, value) => validateContactNumber(value),
              },
            ]}
          >
            <CommonMaskInput
              width="100%"
              placeholder="kanii"
              format="(###) ###-####"
              allowEmptyFormatting
              mask="_"
              customInput={CommonInput}
            />
          </CommonFormItemWithLabel>

          <div id="select">
            <CommonFormItemWithLabel label="Programs" name="programs">
              <CommonSelect
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                notFoundContent={
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No data found."
                  />
                }
                showArrow
                mode="multiple"
                maxTagCount="responsive"
                placeholder="Select Programs"
                options={programs}
                color="primary"
                noIndent={true}
                filterOption={(input: any, option: any) =>
                  (option!.label?.toLowerCase() as unknown as string)?.includes(
                    input?.toLowerCase()
                  )
                }
                onChange={(value: unknown) => {
                  setSelectedPrograms(value as string[]);
                }}
              />
            </CommonFormItemWithLabel>
          </div>

          <CommonFormItemWithLabel
            label="Does customer have allergens?"
            name="has_allergens"
          >
            <CommonSelect
              color="primary"
              placeholder="Select Yes or No"
              onChange={(value: unknown) => {
                setHasAllergens(value === "yes");
              }}
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
            >
              <CommonOption key="yes" value="yes">
                Yes
              </CommonOption>
              <CommonOption key="no" value="no">
                No
              </CommonOption>
            </CommonSelect>
          </CommonFormItemWithLabel>
          {hasAllergens && (
            <div id="select">
              <CommonFormItemWithLabel label="Allergens" name="allergens">
                <CommonSelect
                  getPopupContainer={(triggerNode: HTMLElement) =>
                    triggerNode.parentNode as HTMLElement
                  }
                  notFoundContent={
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No data found."
                    />
                  }
                  showArrow
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="Select Allergens"
                  options={allergens}
                  color="primary"
                  noIndent={true}
                  filterOption={(input: any, option: any) =>
                    (
                      option!.label?.toLowerCase() as unknown as string
                    )?.includes(input?.toLowerCase())
                  }
                />
              </CommonFormItemWithLabel>
            </div>
          )}

          <CommonFormItemWithLabel
            label={"Zip Code"}
            name="zip_code"
            rules={[
              { required: true, message: "Zip Code is required." },
              {
                pattern: /^\d{5}$/,
                message: "Please enter a valid 5-digit zipcode.",
              },
              {
                validator: (rule, value, callback) => {
                  return ValidationUtils.validateAndReplaceNumber(
                    value,
                    form,
                    "zip_code",
                    "Zip Code"
                  );
                },
              },
            ]}
          >
            <CommonInput
              color="primary"
              maxLength={5}
              onInput={(e: any) => {
                TextFormatUtils.upperText(e);
              }}
              placeholder="Input Zip Code"
            />
          </CommonFormItemWithLabel>

          <CommonFormItemWithLabel label={"Address"} name={"address_1"}>
            {renderAddressEdit}
            {renderAddressAdd}
            <CommonFlexContainer justifyCenter>
              <CommonButton
                color="white"
                background="primary"
                onClick={
                  mode === "edit"
                    ? onOpenEditAddressInput
                    : onOpenAddEditAddressMap
                }
                marginRight="10px"
                width="100%"
              >
                {mode === "edit" &&
                editDataAdressModal.address_1 &&
                editDataAdressModal.city
                  ? "Edit Address"
                  : "Add Address"}
              </CommonButton>
              {mode === "edit" &&
                editDataAdressModal.address_1 &&
                editDataAdressModal.city && (
                  <CommonButton onClick={onClickDeleteAddress} width="100%">
                    Delete Address
                  </CommonButton>
                )}
            </CommonFlexContainer>
          </CommonFormItemWithLabel>

          <div id="select">
            <CommonFormItemWithLabel
              label={"Ethnicity"}
              name="ethnicity"
              rules={[{ validator: ValidationUtils.validateLetterInput }]}
            >
              <CommonSelect
                onChange={(data: any) => onEthnicityChange(data)}
                color="primary"
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder="Select Ethnicity"
              >
                {ethnicity.map((item: any) => {
                  return (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  );
                })}
              </CommonSelect>
            </CommonFormItemWithLabel>
          </div>

          {isOther && (
            <CommonFormItemWithLabel
              label={"Others"}
              name="others"
              rules={[
                { required: true, message: "Please input ethnicity." },
                { validator: ValidationUtils.validateInput },
              ]}
            >
              <CommonInput
                placeholder="Input Ethinicity"
                maxLength={50}
                onInput={(e: any) => {
                  TextFormatUtils.upperText(e);
                }}
                color="primary"
              />
            </CommonFormItemWithLabel>
          )}

          <CommonFormItemWithLabel
            label={"Number of Household Members"}
            name="household_member_count"
            rules={[
              {
                validator: (rule, value, callback) => {
                  return ValidationUtils.validateAndReplaceNumber(
                    value,
                    form,
                    "household_member_count"
                  );
                },
              },
            ]}
          >
            <CommonInput
              color="primary"
              maxLength={10}
              placeholder="Input Number of Household Members"
            />
          </CommonFormItemWithLabel>

          <SubscriptionContainer>
            <CheckCircle
              onClick={() => {
                setIsSubscribe(!isSubscribe);
              }}
            >
              {isSubscribe && <CheckIcon size="12px" />}
            </CheckCircle>
            <div>Do you want to subscribe on Growerstats survey?</div>
          </SubscriptionContainer>
        </Form>
      </CommonModal>
      <AddEditAddressModal
        title={"Customer"}
        isAddEditAddressModal={isAddEditAddressModal}
        onCloseAddEditAddressModal={onCloseAddEditAddressModal}
        editDataModal={editDataAdressModal}
        addDataModal={addDataAddressModal}
        mode={mode}
        //setter to Main
        setEditDataModal={setEditDataAdressModal}
        setAddDataModal={setAddDataAddressModal}
      />
    </>
  );
};

export default AddEditCustomerModal;
